import { useNavigate } from 'react-router-dom';
import { ArrowLeft, AlertTriangle, TrendingDown, ShieldOff, DollarSign, Scale } from 'lucide-react';

export default function DisclaimerPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-black text-white">
      <div className="max-w-3xl mx-auto px-4 py-8">
        <button onClick={() => navigate('/dashboard')} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-[#00FF41] transition-colors mb-8" data-testid="disclaimer-back-btn">
          <ArrowLeft className="h-4 w-4" /> Back to Dashboard
        </button>

        <h1 className="text-4xl font-bold mb-2">
          <span className="text-white">Risk </span>
          <span className="text-[#00FF41]">Disclaimer</span>
        </h1>
        <p className="text-gray-400 mb-8">Read this carefully before using BitBot with real money. Last updated: February 2026.</p>

        <div className="border-2 border-red-600 bg-red-950/30 p-5 mb-10">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-6 w-6 text-red-400 shrink-0 mt-0.5" />
            <div>
              <h2 className="text-red-300 font-bold text-lg mb-2">CRYPTOCURRENCY TRADING IS HIGH RISK</h2>
              <p className="text-red-200 text-sm leading-relaxed">
                You can — and many people do — lose money trading cryptocurrency. Past performance is not a guarantee
                of future results. The crypto market is volatile, unregulated in most jurisdictions, and influenced by
                forces that are not always rational or predictable.
                <br /><br />
                <strong>Never trade with money you cannot afford to lose.</strong>
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-8">
          <section>
            <h2 className="text-xl font-bold text-[#00FF41] mb-3 flex items-center gap-2">
              <Scale className="h-5 w-5" /> Not Financial Advice
            </h2>
            <p className="text-gray-300 leading-relaxed">
              BitBot is a software tool. Nothing on this platform — including the trade signals, RSI indicators,
              backtest results, AI Assistant responses, blog posts, FAQs, or in-app suggestions — constitutes
              financial, investment, legal, accounting, or tax advice. The platform is for informational and
              educational purposes only.
            </p>
            <p className="text-gray-300 leading-relaxed mt-3">
              We are not a registered investment advisor, broker-dealer, or financial planner. Any decision you make
              regarding which coins to trade, how much capital to allocate, what RSI thresholds to use, or whether to
              run the bot in live mode is entirely your own.
            </p>
            <p className="text-gray-300 leading-relaxed mt-3">
              Consult a licensed financial professional in your jurisdiction before making any investment decisions
              based on data or signals presented in BitBot.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-[#00FF41] mb-3 flex items-center gap-2">
              <TrendingDown className="h-5 w-5" /> Trading Risk
            </h2>
            <p className="text-gray-300 leading-relaxed mb-3">
              You acknowledge and understand that:
            </p>
            <ul className="text-gray-300 leading-relaxed list-disc list-inside space-y-2 pl-2">
              <li>
                <strong>Loss of capital is possible and likely at some point.</strong> Even profitable strategies
                experience drawdowns. The bot can — and at times will — generate losing trades.
              </li>
              <li>
                <strong>Past performance does not predict future returns.</strong> Backtest data, simulated returns,
                and any historical statistics shown in the app reflect what would have happened under specific
                market conditions. The market changes. Strategies that worked in the past may not work in the future.
              </li>
              <li>
                <strong>Slippage, partial fills, and order rejection can occur.</strong> Coinbase and other exchanges
                do not always fill at the price the bot calculated. Real-world execution differs from simulation.
              </li>
              <li>
                <strong>Fees compound.</strong> Every buy and sell incurs an exchange fee (currently around 1.2%–1.6%
                per side on Coinbase Advanced for retail). High-frequency strategies can be eaten alive by fees.
              </li>
              <li>
                <strong>Exchange risk exists.</strong> If Coinbase goes offline, has a security breach, freezes your
                account, or changes API rules, your funds and your bot may be impacted. Spread your risk; never keep
                more on an exchange than you can lose.
              </li>
              <li>
                <strong>Black swan events happen.</strong> Flash crashes, exchange hacks, regulatory shutdowns, stablecoin
                de-pegs, and overnight collapses are all real and ongoing risks in the crypto market.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-[#00FF41] mb-3 flex items-center gap-2">
              <ShieldOff className="h-5 w-5" /> Software Limitations
            </h2>
            <p className="text-gray-300 leading-relaxed">
              BitBot is software. Software has bugs. Software relies on external services (Coinbase API, price
              feeds, hosting infrastructure) that can fail, lag, or return incorrect data. We work hard to make
              the platform reliable, but we cannot guarantee:
            </p>
            <ul className="text-gray-300 leading-relaxed list-disc list-inside space-y-2 pl-2 mt-3">
              <li>That trades will execute when expected</li>
              <li>That price data is accurate or current at any given moment</li>
              <li>That the bot's calculations match what Coinbase actually fills</li>
              <li>That notifications will be delivered (email/SMS providers can fail)</li>
              <li>That the platform will be available 24/7 (maintenance, outages, and hosting issues happen)</li>
            </ul>
            <p className="text-gray-300 leading-relaxed mt-3">
              You are responsible for monitoring your account and for stopping the bot if you observe behavior you
              do not expect. The kill switch is in the dashboard for a reason.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-[#00FF41] mb-3 flex items-center gap-2">
              <DollarSign className="h-5 w-5" /> Tax Responsibility
            </h2>
            <p className="text-gray-300 leading-relaxed">
              Cryptocurrency trades are taxable events in most jurisdictions, including the United States. You are
              solely responsible for tracking your gains and losses, calculating your tax obligation, and reporting
              correctly to your local tax authority.
            </p>
            <p className="text-gray-300 leading-relaxed mt-3">
              The Tax Report feature in BitBot is provided as a convenience to help you organize your trade history.
              It is <strong>not</strong> a substitute for professional tax preparation. Consult a CPA or qualified
              tax preparer, especially if you trade significant volume.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-[#00FF41] mb-3">No Affiliation</h2>
            <p className="text-gray-300 leading-relaxed">
              BitBot is an independent project. We are not affiliated with, endorsed by, or partnered with
              Coinbase, Binance, Kraken, the Bitcoin Foundation, the Ethereum Foundation, or any other exchange
              or cryptocurrency project. We use their public APIs and reference their assets, but we operate
              independently.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-[#00FF41] mb-3">Jurisdiction</h2>
            <p className="text-gray-300 leading-relaxed">
              Cryptocurrency regulation varies wildly by country and by US state. It is your responsibility to
              ensure that automated trading is legal in your jurisdiction. If it is not, do not use BitBot in
              live mode. Simulation mode does not execute real trades and is available everywhere.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-[#00FF41] mb-3">Limitation of Liability</h2>
            <p className="text-gray-300 leading-relaxed">
              By using BitBot you agree that the operators, contributors, and any associated parties are not liable
              for any losses you incur — direct, indirect, consequential, financial, emotional, or otherwise —
              resulting from your use of the platform, the failure of the platform, the failure of any connected
              exchange, or any decision you make based on information presented by the platform.
            </p>
            <p className="text-gray-300 leading-relaxed mt-3">
              See our full <button onClick={() => navigate('/terms')} className="text-[#00FF41] hover:underline">Terms of Service</button> for the
              complete legal language.
            </p>
          </section>

          <section className="border border-yellow-700/50 bg-yellow-950/20 p-4 text-sm text-yellow-200">
            <strong>The short version:</strong> Crypto is risky. Bots can lose money. You are responsible for your
            own trades and your own taxes. We make no guarantees. If that's a deal-breaker for you, don't use this
            tool in live mode. Stick with simulation, which is free and risk-free.
          </section>
        </div>

        <footer className="mt-12 pt-6 border-t border-[#003B00] text-xs text-gray-600 flex flex-wrap gap-4">
          <button onClick={() => navigate('/about')} className="hover:text-[#00FF41]">About</button>
          <button onClick={() => navigate('/faq')} className="hover:text-[#00FF41]">FAQ</button>
          <button onClick={() => navigate('/contact')} className="hover:text-[#00FF41]">Contact</button>
          <button onClick={() => navigate('/privacy')} className="hover:text-[#00FF41]">Privacy</button>
          <button onClick={() => navigate('/terms')} className="hover:text-[#00FF41]">Terms</button>
          <button onClick={() => navigate('/cookies')} className="hover:text-[#00FF41]">Cookies</button>
        </footer>
      </div>
    </div>
  );
}
